import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { SecureStorage } from '../utils/secure-storage';
import { analyticsCacheUtils } from '../hooks/useAnalyticsCache';

// Интерфейс для конфигурации кэширования
interface CacheConfig {
  enabled: boolean;
  ttl: number; // Time to live в миллисекундах
  keyGenerator?: (config: AxiosRequestConfig) => string;
}

// Конфигурация кэширования по умолчанию
const DEFAULT_CACHE_CONFIG: CacheConfig = {
  enabled: true,
  ttl: 5 * 60 * 1000, // 5 минут
  keyGenerator: (config) => `${config.method?.toUpperCase()}_${config.url}_${JSON.stringify(config.params || {})}`
};

// Глобальный кэш для HTTP запросов
const httpCache = new Map<string, { data: any; timestamp: number; ttl: number }>();

// AD-747(б). Поколение кэша: `invalidateCache()` начинает новое. Ответ на запрос,
// начатый в прежнем поколении (был в пути при смене пользователя или при записи),
// отдаётся вызвавшему, но в кэш не ложится — иначе ответ прежнего пользователя
// попал бы в уже очищенный кэш и достался следующему. Общее для всех экземпляров
// клиента, как и сам кэш.
let cacheGeneration = 0;

/**
 * Оптимизированный HTTP клиент с кэшированием
 * Автоматически кэширует GET запросы и управляет TTL
 */
class CachedHttpClient {
  private client: AxiosInstance;
  private cacheConfig: CacheConfig;

  constructor(baseURL: string, cacheConfig: Partial<CacheConfig> = {}) {
    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.cacheConfig = { ...DEFAULT_CACHE_CONFIG, ...cacheConfig };

    // Добавляем интерцепторы
    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Интерцептор запросов (async для Secure Storage)
    this.client.interceptors.request.use(
      async (config) => {
        const token = await SecureStorage.get('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Логирование запросов
        return config;
      },
      (error) => {
        console.error('❌ CachedHttpClient: Ошибка запроса:', error);
        return Promise.reject(error);
      }
    );

    // Интерцептор ответов
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        console.error('❌ CachedHttpClient: Ошибка ответа:', error);
        return Promise.reject(error);
      }
    );
  }

  // Проверка валидности кэша
  private isCacheValid(entry: { data: any; timestamp: number; ttl: number }): boolean {
    const now = Date.now();
    return (now - entry.timestamp) < entry.ttl;
  }

  // Генерация ключа кэша
  private generateCacheKey(config: AxiosRequestConfig): string {
    if (this.cacheConfig.keyGenerator) {
      return this.cacheConfig.keyGenerator(config);
    }
    return `${config.method?.toUpperCase()}_${config.url}_${JSON.stringify(config.params || {})}`;
  }

  // Очистка устаревших записей кэша
  private cleanupExpiredCache(): void {
    const now = Date.now();
    Array.from(httpCache.entries()).forEach(([key, entry]) => {
      if ((now - entry.timestamp) >= entry.ttl) {
        httpCache.delete(key);
      }
    });
  }

  // GET запрос с кэшированием
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const requestConfig = { ...config, method: 'GET' as const, url };
    
    // Проверяем кэш только для GET запросов
    if (this.cacheConfig.enabled && requestConfig.method === 'GET') {
      const cacheKey = this.generateCacheKey(requestConfig);
      const cachedEntry = httpCache.get(cacheKey);
      
      if (cachedEntry && this.isCacheValid(cachedEntry)) {
        return {
          data: cachedEntry.data,
          status: 200,
          statusText: 'OK',
          headers: {},
          config: requestConfig
        } as AxiosResponse<T>;
      }
    }

    try {
      // Выполняем запрос
      const generation = cacheGeneration;
      const response = await this.client.get<T>(url, config);

      // Кэшируем успешные GET запросы, если кэш не сбросили, пока запрос был в пути
      if (
        this.cacheConfig.enabled &&
        requestConfig.method === 'GET' &&
        response.status === 200 &&
        generation === cacheGeneration
      ) {
        const cacheKey = this.generateCacheKey(requestConfig);
        httpCache.set(cacheKey, {
          data: response.data,
          timestamp: Date.now(),
          ttl: this.cacheConfig.ttl
        });
        
        // Очищаем устаревшие записи
        this.cleanupExpiredCache();
      }
      
      return response;
    } catch (error) {
      console.error(`❌ CachedHttpClient: Ошибка GET запроса ${url}:`, error);
      throw error;
    }
  }

  // POST запрос (без кэширования)
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      const response = await this.client.post<T>(url, data, config);
      
      // Очищаем кэш при изменении данных
      this.invalidateCache();
      
      return response;
    } catch (error) {
      console.error(`❌ CachedHttpClient: Ошибка POST запроса ${url}:`, error);
      throw error;
    }
  }

  // PUT запрос (без кэширования)
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      const response = await this.client.put<T>(url, data, config);
      
      // Очищаем кэш при изменении данных
      this.invalidateCache();
      
      return response;
    } catch (error) {
      console.error(`❌ CachedHttpClient: Ошибка PUT запроса ${url}:`, error);
      throw error;
    }
  }

  // DELETE запрос (без кэширования)
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      const response = await this.client.delete<T>(url, config);
      
      // Очищаем кэш при изменении данных
      this.invalidateCache();
      
      return response;
    } catch (error) {
      console.error(`❌ CachedHttpClient: Ошибка DELETE запроса ${url}:`, error);
      throw error;
    }
  }

  // Очистка кэша
  invalidateCache(): void {
    cacheGeneration += 1;
    httpCache.clear();
    analyticsCacheUtils.clearAll();
  }

  // Очистка кэша по паттерну
  invalidateCacheByPattern(pattern: string): void {
    const regex = new RegExp(pattern);
    Array.from(httpCache.keys()).forEach(key => {
      if (regex.test(key)) {
        httpCache.delete(key);
      }
    });
  }

  // Получение статистики кэша
  getCacheStats() {
    const now = Date.now();
    const totalEntries = httpCache.size;
    const validEntries = Array.from(httpCache.values()).filter(entry => 
      (now - entry.timestamp) < entry.ttl
    ).length;
    const expiredEntries = totalEntries - validEntries;

    return {
      totalEntries,
      validEntries,
      expiredEntries,
      cacheSize: httpCache.size,
      keys: Array.from(httpCache.keys())
    };
  }

  // Предзагрузка данных
  async preload<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.get<T>(url, config);
      return response.data;
    } catch (error) {
      console.error(`❌ CachedHttpClient: Ошибка предзагрузки ${url}:`, error);
      throw error;
    }
  }

  // Получение базового клиента для прямого доступа
  getClient(): AxiosInstance {
    return this.client;
  }
}

// Создаем экземпляр клиента
const cachedHttpClient = new CachedHttpClient(
  process.env['REACT_APP_API_BASE_URL'] || 'http://localhost:3001/api',
  {
    enabled: true,
    ttl: 5 * 60 * 1000, // 5 минут
  }
);

export default cachedHttpClient;
export { CachedHttpClient };
